import React from 'react';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon, 
  PlusIcon, 
  EyeIcon, 
  CheckCircleIcon,
  UserIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  SparklesIcon,
  FireIcon,
  ArrowRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const LeadModals = ({
  // Modal states
  showLeadModal,
  showClientModal,
  showDetailModal,
  showAssignModal,
  
  // Modal handlers
  closeLeadModal,
  handleCloseClientModal,
  closeDetailModal,
  closeAssignModal,
  
  // Form data and handlers
  step,
  setStep,
  formData,
  handleInputChange,
  clientSelected,
  vetturaSelected,
  searchTerm,
  setSearchTerm,
  clientSearchResults,
  searchClients,
  handleSelectClient,
  handleRemoveClient,
  fonti,
  dettagliProvenienza,
  vettureSearchTerm,
  setVettureSearchTerm,
  vettureSearchResults,
  searchVetture,
  handleSelectVettura,
  handleRemoveVettura,
  handleSubmit,
  loadingModal,
  
  // Client modal
  clientFormData,
  handleClientFormChange,
  handleCreateClient,
  loadingClientCreation,
  handleOpenClientModal,
  
  // Detail modal
  selectedLead,
  getVehicleImageUrl,
  handleOpenAssignModal,
  
  // Assignment modal
  selectedLeads,
  agenzieDisponibili,
  selectedAgenzia,
  setSelectedAgenzia,
  noteAssegnazione,
  setNoteAssegnazione,
  agenzieSearchTerm,
  setAgenzieSearchTerm,
  agenzieOrderBy,
  setAgenzieOrderBy,
  handleAssegnazione,
  loadingAssegnazione
}) => {
  return (
    <>
      {/* Modal Creazione Lead */}
      {showLeadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={closeLeadModal}></div>
            
            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full border border-white/20">
              {/* Header */}
              <div className="flex justify-between items-center p-8 border-b border-gray-100/50 bg-gradient-to-r from-blue-50/50 to-white/50">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Creazione Nuova Lead</h1>
                  <p className="text-gray-600 mt-1">Inserisci i dettagli per creare una nuova opportunità commerciale</p>
                </div>
                <button
                  type="button"
                  className="p-3 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all duration-200"
                  onClick={closeLeadModal}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="px-8 py-6 bg-gradient-to-r from-gray-50/50 to-white/50 border-b border-gray-100/50">
                <div className="flex items-center justify-between">
                  {[
                    { number: 1, title: 'Cliente', active: step === 1, completed: clientSelected },
                    { number: 2, title: 'Provenienza', active: step === 2, completed: formData.provenienza.fonte },
                    { number: 3, title: 'Dettagli', active: step === 3, completed: false }
                  ].map((stepItem, index) => (
                    <div key={stepItem.number} className="flex items-center">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-2xl font-bold text-sm transition-all duration-300 ${
                        stepItem.completed ? 'bg-green-500 text-white' :
                        stepItem.active ? 'bg-blue-500 text-white' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {stepItem.completed ? '✓' : stepItem.number}
                      </div>
                      <span className={`ml-3 font-medium ${
                        stepItem.active ? 'text-blue-600' : 
                        stepItem.completed ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {stepItem.title}
                      </span>
                      {index < 2 && (
                        <div className={`mx-6 w-16 h-1 rounded-full transition-all duration-300 ${
                          stepItem.completed ? 'bg-green-500' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Body */}
              <div className="p-8">
                {/* Step 1: Cliente */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Cerca Cliente <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className="w-full pl-12 pr-4 py-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg"
                          placeholder="Cerca per codice cliente, ragione sociale, p.iva..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            searchClients(e.target.value);
                          }}
                          disabled={clientSelected}
                        />
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
                      </div>
                      
                      {/* Results */}
                      {!clientSelected && searchTerm.length >= 3 && (
                        <div className="mt-4 bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 max-h-80 overflow-y-auto shadow-lg">
                          {clientSearchResults.length > 0 ? (
                            <div className="divide-y divide-gray-100/50">
                              {clientSearchResults.map((client) => (
                                <div 
                                  key={client.id}
                                  className="p-4 hover:bg-blue-50/50 cursor-pointer transition-all duration-200"
                                  onClick={() => handleSelectClient(client)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="font-semibold text-gray-900">
                                        {client.nome} {client.ragioneSociale}
                                      </div>
                                      <div className="text-sm text-gray-500 mt-1">
                                        {client.codiceFiscale}
                                      </div>
                                    </div>
                                    <div className="text-sm font-medium text-blue-600">
                                      {client.idCliente}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Create New Client Option */}
                              <div 
                                className="p-4 hover:bg-green-50/50 cursor-pointer border-t-2 border-green-100/50 transition-all duration-200"
                                onClick={handleOpenClientModal}
                              >
                                <div className="flex items-center text-green-600">
                                  <PlusIcon className="h-5 w-5 mr-3" />
                                  <div>
                                    <div className="font-semibold">Crea Nuovo Cliente</div>
                                    <div className="text-sm text-green-500 mt-1">
                                      Non trovi il cliente? Creane uno nuovo
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="p-8 text-center">
                              <div className="text-gray-500 mb-4">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
                                  <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p>Nessun cliente trovato per "{searchTerm}"</p>
                              </div>
                              <button
                                type="button"
                                className="inline-flex items-center px-6 py-3 border border-green-300 rounded-2xl text-sm font-semibold text-green-700 bg-green-50 hover:bg-green-100 transition-all duration-200"
                                onClick={handleOpenClientModal}
                              >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Crea Nuovo Cliente
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Client */}
                    {clientSelected && (
                      <div className="bg-blue-50/50 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 relative">
                        <button
                          type="button"
                          className="absolute top-4 right-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200"
                          onClick={handleRemoveClient}
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                                                  <div className="flex items-center space-x-3 mb-4">
                            <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                              <CheckCircleIcon className="h-4 w-4 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Cliente Selezionato</h3>
                          </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-500">Nome</p>
                            <p className="text-lg font-semibold text-gray-900">{formData.cliente.nome}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Ragione Sociale</p>
                            <p className="text-lg font-semibold text-gray-900">{formData.cliente.ragioneSociale}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Codice Fiscale</p>
                            <p className="text-lg font-semibold text-gray-900">{formData.cliente.codiceFiscale}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Step 2: Provenienza */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Fonte Provenienza <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg"
                        value={formData.provenienza.fonte}
                        onChange={(e) => handleInputChange('provenienza', 'fonte', e.target.value)}
                        required
                      >
                        <option value="">Seleziona fonte provenienza</option>
                        {fonti.map((fonte) => (
                          <option key={fonte.id} value={fonte.id}>
                            {fonte.descrizione}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Dettaglio Provenienza
                      </label>
                      <select
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg disabled:opacity-50"
                        value={formData.provenienza.dettaglio}
                        onChange={(e) => handleInputChange('provenienza', 'dettaglio', e.target.value)}
                        disabled={!formData.provenienza.fonte || dettagliProvenienza.length === 0}
                      >
                        <option value="">Seleziona dettaglio provenienza</option>
                        {dettagliProvenienza.map((dettaglio) => (
                          <option key={dettaglio.id} value={dettaglio.id}>
                            {dettaglio.descrizione}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Vettura di Interesse */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Vettura di Interesse <span className="text-gray-500 font-normal">(opzionale)</span>
                      </label>
                      
                      {!vetturaSelected ? (
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full pl-12 pr-4 py-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg"
                            placeholder="Cerca per marca, modello..."
                            value={vettureSearchTerm}
                            onChange={(e) => {
                              setVettureSearchTerm(e.target.value);
                              searchVetture(e.target.value);
                            }}
                          />
                          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
                          
                          {/* Vehicle Results */}
                          {vettureSearchResults.length > 0 && (
                            <div className="mt-4 bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 max-h-60 overflow-y-auto shadow-lg">
                              <div className="divide-y divide-gray-100/50">
                                {vettureSearchResults.map((vettura) => (
                                  <div 
                                    key={vettura.id}
                                    className="p-4 hover:bg-blue-50/50 cursor-pointer transition-all duration-200"
                                    onClick={() => handleSelectVettura(vettura)}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-semibold text-gray-900">
                                          {vettura.brand} {vettura.modello}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                          {vettura.alimentazione} • {vettura.trasmissione}
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        ID: {vettura.id}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-green-50/50 backdrop-blur-sm rounded-2xl p-6 border border-green-200/50 relative">
                          <button
                            type="button"
                            className="absolute top-4 right-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200"
                            onClick={handleRemoveVettura}
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                              <CheckCircleIcon className="h-4 w-4 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Vettura Selezionata</h3>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Marca e Modello</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.vettura.brand} {formData.vettura.modello}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Alimentazione</p>
                              <p className="text-lg font-semibold text-gray-900">{formData.vettura.alimentazione}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Step 3: Dettagli */}
                {step === 3 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Data Creazione <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg"
                          value={formData.dettagli.dataCreazione}
                          onChange={(e) => handleInputChange('dettagli', 'dataCreazione', e.target.value)}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Ora Creazione <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="time"
                          className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg"
                          value={formData.dettagli.oraCreazione}
                          onChange={(e) => handleInputChange('dettagli', 'oraCreazione', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Note <span className="text-gray-500 font-normal">(max 250 caratteri)</span>
                      </label>
                      <textarea
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 text-lg resize-none"
                        rows="4"
                        value={formData.dettagli.note}
                        onChange={(e) => handleInputChange('dettagli', 'note', e.target.value)}
                        maxLength={250}
                        placeholder="Inserisci eventuali note aggiuntive..."
                      />
                      <div className="text-right text-sm text-gray-500 mt-2">
                        {formData.dettagli.note?.length || 0}/250
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="flex justify-between items-center p-8 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <div className="flex space-x-3">
                  {step > 1 && (
                    <button
                      type="button"
                      className="px-6 py-3 bg-white/70 border border-gray-200/50 text-gray-700 font-semibold rounded-2xl hover:bg-white/90 hover:shadow-md transition-all duration-200"
                      onClick={() => setStep(step - 1)}
                    >
                      Indietro
                    </button>
                  )}
                </div>
                
                <button
                  type="button"
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSubmit(step === 3)}
                  disabled={loadingModal || (step === 1 && !clientSelected) || (step === 2 && !formData.provenienza.fonte)}
                >
                  {loadingModal ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Salvataggio...
                    </div>
                  ) : step === 3 ? 'Crea Lead' : 'Continua'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dettaglio Lead */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={closeDetailModal}></div>
            
            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-white/20">
              {/* Header */}
              <div className="flex justify-between items-center p-8 border-b border-gray-100/50 bg-gradient-to-r from-blue-50/50 to-white/50">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Dettaglio Lead</h1>
                  <p className="text-blue-600 font-semibold mt-1">
                    LEAD {selectedLead.id.toString().padStart(8, '0')}
                    {selectedLead.idPratica && (
                      <span className="text-green-600"> • {selectedLead.idPratica}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {selectedLead.isAssegnata && (
                    <div className="px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-2xl">
                      ASSEGNATA
                    </div>
                  )}
                  <button
                    type="button"
                    className="p-3 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all duration-200"
                    onClick={closeDetailModal}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Lead Info */}
                  <div className="space-y-6">
                    {/* Client Info */}
                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-2xl p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Informazioni Cliente</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Codice Cliente</p>
                          <p className="text-lg font-semibold text-gray-900">{selectedLead.idCliente || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Ragione Sociale</p>
                          <p className="text-lg font-semibold text-gray-900">{selectedLead.ragioneSociale || 'Non specificata'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Lead Details */}
                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-2xl p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                          <DocumentTextIcon className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Dettagli Lead</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-500">Provenienza</span>
                          <span className="text-lg font-semibold text-gray-900">{selectedLead.provenienza || 'Non specificata'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-500">Data Creazione</span>
                          <span className="text-lg font-semibold text-gray-900">{selectedLead.dataCreazione}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-500">Temperatura</span>
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                              selectedLead.temperaturaLead === 'Caldo' ? 'bg-gradient-to-br from-red-500 to-red-600' : 
                              selectedLead.temperaturaLead === 'Tiepido' ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 
                              'bg-gradient-to-br from-blue-500 to-blue-600'
                            }`}>
                              {selectedLead.temperaturaLead === 'Caldo' ? (
                                <FireIcon className="h-5 w-5 text-white" />
                              ) : selectedLead.temperaturaLead === 'Tiepido' ? (
                                <SparklesIcon className="h-5 w-5 text-white" />
                              ) : (
                                <div className="w-3 h-3 bg-white rounded-full"></div>
                              )}
                            </div>
                            <span className="text-lg font-semibold text-gray-900">{selectedLead.temperaturaLead || 'Media'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {selectedLead.note && (
                        <div className="mt-6 p-4 bg-white/70 rounded-xl border border-gray-200/50">
                          <p className="text-sm font-medium text-gray-500 mb-2">Note</p>
                          <p className="text-gray-900">{selectedLead.note}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Vehicle */}
                  <div className="space-y-6">
                    {selectedLead.vetturaInteresse ? (
                      <div className="bg-blue-50/50 backdrop-blur-sm rounded-2xl p-6">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <TruckIcon className="h-5 w-5 text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900">Vettura di Interesse</h3>
                        </div>
                        
                        {getVehicleImageUrl(selectedLead) ? (
                          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                            <img
                              src={getVehicleImageUrl(selectedLead)}
                              alt={selectedLead.vetturaInteresse}
                              className="w-full h-48 object-contain rounded-xl"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Crect width='400' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3EImmagine Non Disponibile%3C/text%3E%3C/svg%3E";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="bg-white rounded-2xl p-8 shadow-sm mb-4">
                            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                                <EyeIcon className="h-8 w-8" />
                              </div>
                              <p className="text-sm">Immagine non disponibile</p>
                            </div>
                          </div>
                        )}

                        <div className="bg-white rounded-2xl p-4 shadow-sm">
                          <h4 className="font-bold text-gray-900 text-lg mb-4">{selectedLead.vetturaInteresse}</h4>
                          
                          {selectedLead.vettura && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-500">Marca</p>
                                <p className="text-lg font-semibold text-gray-900">{selectedLead.vettura.brand || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-500">Modello</p>
                                <p className="text-lg font-semibold text-gray-900">{selectedLead.vettura.modello || 'N/A'}</p>
                              </div>
                              {selectedLead.vettura.alimentazione && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Alimentazione</p>
                                  <p className="text-lg font-semibold text-gray-900">{selectedLead.vettura.alimentazione}</p>
                                </div>
                              )}
                              {selectedLead.vettura.trasmissione && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Trasmissione</p>
                                  <p className="text-lg font-semibold text-gray-900">{selectedLead.vettura.trasmissione}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50/50 backdrop-blur-sm rounded-2xl p-8 text-center">
                        <div className="w-16 h-16 bg-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <EyeIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500">Nessuna vettura di interesse specificata</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end items-center space-x-4 p-8 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <button
                  type="button"
                  className="px-6 py-3 bg-white/70 border border-gray-200/50 text-gray-700 font-semibold rounded-2xl hover:bg-white/90 hover:shadow-md transition-all duration-200"
                  onClick={closeDetailModal}
                >
                  Chiudi
                </button>
                {!selectedLead.isAssegnata ? (
                  <button
                    type="button"
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-2xl hover:from-green-700 hover:to-green-800 hover:shadow-lg transition-all duration-200"
                    onClick={() => handleOpenAssignModal(selectedLead.id)}
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2 inline" />
                    Assegna ad Agenzia
                  </button>
                ) : (
                  <div className="px-6 py-3 bg-green-100 text-green-700 font-semibold rounded-2xl border border-green-200">
                    Lead già assegnata
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Creazione Cliente */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={handleCloseClientModal}></div>
            
            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-white/20">
              {/* Header */}
              <div className="flex justify-between items-center p-8 border-b border-gray-100/50 bg-gradient-to-r from-green-50/50 to-white/50">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Crea Nuovo Cliente</h1>
                  <p className="text-gray-600 mt-1">Inserisci i dati del nuovo cliente</p>
                </div>
                <button
                  type="button"
                  className="p-3 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all duration-200"
                  onClick={handleCloseClientModal}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-8">
                <div className="space-y-6">
                  {/* Tipo Cliente */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Tipo Cliente <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="tipo_cliente"
                          value="privato"
                          checked={clientFormData.tipo_cliente === 'privato'}
                          onChange={(e) => handleClientFormChange('tipo_cliente', e.target.value)}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-400"
                        />
                        <span className="text-gray-900 font-medium">Privato</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="tipo_cliente"
                          value="azienda"
                          checked={clientFormData.tipo_cliente === 'azienda'}
                          onChange={(e) => handleClientFormChange('tipo_cliente', e.target.value)}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-400"
                        />
                        <span className="text-gray-900 font-medium">Azienda</span>
                      </label>
                    </div>
                  </div>

                  {/* Campi Dinamici basati sul tipo */}
                  {clientFormData.tipo_cliente === 'privato' ? (
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Nome <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                          value={clientFormData.nome}
                          onChange={(e) => handleClientFormChange('nome', e.target.value)}
                          placeholder="Nome"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Cognome <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                          value={clientFormData.cognome}
                          onChange={(e) => handleClientFormChange('cognome', e.target.value)}
                          placeholder="Cognome"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Ragione Sociale <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.ragione_sociale}
                        onChange={(e) => handleClientFormChange('ragione_sociale', e.target.value)}
                        placeholder="Ragione Sociale"
                      />
                    </div>
                  )}

                  {/* Email e Telefono */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.email}
                        onChange={(e) => handleClientFormChange('email', e.target.value)}
                        placeholder="email@esempio.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Telefono <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.telefono}
                        onChange={(e) => handleClientFormChange('telefono', e.target.value)}
                        placeholder="+39 123 456 7890"
                      />
                    </div>
                  </div>

                  {/* Codice Fiscale e Partita IVA */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Codice Fiscale
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.codice_fiscale}
                        onChange={(e) => handleClientFormChange('codice_fiscale', e.target.value.toUpperCase())}
                        placeholder="RSSMRA80A01H501Z"
                        maxLength={16}
                      />
                    </div>
                    {clientFormData.tipo_cliente === 'azienda' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Partita IVA
                        </label>
                        <input
                          type="text"
                          className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                          value={clientFormData.partita_iva}
                          onChange={(e) => handleClientFormChange('partita_iva', e.target.value)}
                          placeholder="12345678901"
                          maxLength={11}
                        />
                      </div>
                    )}
                  </div>

                  {/* Tipo Società Dettaglio - Solo per Aziende */}
                  {clientFormData.tipo_cliente === 'azienda' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Tipologia Azienda <span className="text-red-500">*</span>
                        <span className="text-xs font-normal text-gray-500 ml-2">(Determina i documenti richiesti)</span>
                      </label>
                      <select
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.tipo_societa_dettaglio || ''}
                        onChange={(e) => handleClientFormChange('tipo_societa_dettaglio', e.target.value)}
                        required
                      >
                        <option value="">Seleziona tipologia...</option>
                        <optgroup label="Professionisti e Imprese Individuali">
                          <option value="libero_professionista">Libero Professionista</option>
                          <option value="ditta_individuale">Ditta Individuale</option>
                        </optgroup>
                        <optgroup label="Società">
                          <option value="societa_persone">Società di Persone (SNC, SAS)</option>
                          <option value="societa_capitali">Società di Capitali (SRL, SPA, SAPA)</option>
                          <option value="societa_estere">Società Estera</option>
                        </optgroup>
                        <optgroup label="Enti e Altri">
                          <option value="consorzi_onlus">Consorzio / ONLUS / Associazione</option>
                          <option value="pensionati">Pensionato (come azienda)</option>
                        </optgroup>
                      </select>
                      <p className="text-xs text-gray-500 mt-2">
                        ⚠️ Questa selezione determina quali documenti saranno richiesti per la pratica
                      </p>
                    </div>
                  )}

                  {/* Indirizzo */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Indirizzo
                    </label>
                    <input
                      type="text"
                      className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                      value={clientFormData.indirizzo}
                      onChange={(e) => handleClientFormChange('indirizzo', e.target.value)}
                      placeholder="Via Roma 123"
                    />
                  </div>

                  {/* Città, CAP, Provincia */}
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Città
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.citta}
                        onChange={(e) => handleClientFormChange('citta', e.target.value)}
                        placeholder="Milano"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        CAP
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.cap}
                        onChange={(e) => handleClientFormChange('cap', e.target.value)}
                        placeholder="20100"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Provincia
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                        value={clientFormData.provincia}
                        onChange={(e) => handleClientFormChange('provincia', e.target.value.toUpperCase())}
                        placeholder="MI"
                        maxLength={2}
                      />
                    </div>
                  </div>

                  {/* Note */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Note
                    </label>
                    <textarea
                      className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 resize-none"
                      rows="3"
                      value={clientFormData.note}
                      onChange={(e) => handleClientFormChange('note', e.target.value)}
                      placeholder="Note aggiuntive..."
                    />
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex justify-between items-center p-8 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <button
                  type="button"
                  className="px-6 py-3 bg-white/70 border border-gray-200/50 text-gray-700 font-semibold rounded-2xl hover:bg-white/90 hover:shadow-md transition-all duration-200"
                  onClick={handleCloseClientModal}
                >
                  Annulla
                </button>
                
                <button
                  type="button"
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-2xl hover:from-green-700 hover:to-green-800 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCreateClient}
                  disabled={loadingClientCreation}
                >
                  {loadingClientCreation ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Creazione...
                    </div>
                  ) : 'Crea Cliente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Assegnazione */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={closeAssignModal}></div>
            
            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-white/20">
              {/* Header */}
              <div className="flex justify-between items-center p-8 border-b border-gray-100/50 bg-gradient-to-r from-green-50/50 to-white/50">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Assegna ad Agenzia</h1>
                  <p className="text-gray-600 mt-1">
                    {selectedLeads.length === 1 ? 'Assegnazione singola' : `Assegnazione multipla (${selectedLeads.length} lead)`}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-3 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all duration-200"
                  onClick={closeAssignModal}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-8">
                <div className="space-y-6">
                  {/* Selezione Agenzia */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Seleziona Agenzia Primaria <span className="text-red-500">*</span>
                    </label>
                    
                    {agenzieDisponibili.length === 0 ? (
                      <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-2xl">
                        <div className="text-center">
                          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessuna agenzia disponibile</h3>
                          <p className="text-gray-500">Non ci sono agenzie primarie attive nel sistema.</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Barra di ricerca */}
                        <div className="mb-4">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full pl-12 pr-4 py-3 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                              placeholder="Cerca agenzie per nome o città..."
                              value={agenzieSearchTerm}
                              onChange={(e) => setAgenzieSearchTerm(e.target.value)}
                            />
                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* Lista Agenzie */}
                        <div className="space-y-3 max-h-96 overflow-y-auto apple-scrollbar">
                          {(() => {
                            let agenzieFiltered = agenzieDisponibili.filter(agenzia => {
                              if (!agenzieSearchTerm) return true;
                              const searchLower = agenzieSearchTerm.toLowerCase();
                              return (
                                agenzia.nome?.toLowerCase().includes(searchLower) ||
                                agenzia.citta?.toLowerCase().includes(searchLower) ||
                                agenzia.utente?.nome?.toLowerCase().includes(searchLower) ||
                                agenzia.utente?.cognome?.toLowerCase().includes(searchLower)
                              );
                            });
                            
                            if (agenzieFiltered.length === 0 && agenzieSearchTerm) {
                              return (
                                <div className="text-center py-8">
                                  <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-400 mb-4" />
                                  <p className="text-gray-500">
                                    Nessuna agenzia trovata per "{agenzieSearchTerm}"
                                  </p>
                                </div>
                              );
                            }
                            
                            return agenzieFiltered.map((agenzia) => {
                              const isSelected = selectedAgenzia == agenzia.id;
                              const stats = agenzia.statistiche || {};
                              
                              return (
                                <div
                                  key={agenzia.id}
                                  className={`relative rounded-2xl border-2 p-4 cursor-pointer transition-all duration-200 ${
                                    isSelected 
                                      ? 'border-green-500 bg-green-50/50 shadow-lg' 
                                      : 'border-gray-200/50 hover:border-green-300 hover:shadow-md hover:bg-green-50/20'
                                  }`}
                                  onClick={() => setSelectedAgenzia(agenzia.id)}
                                >
                                  {/* Indicatore di selezione */}
                                  <div className="absolute top-4 right-4">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                      isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                                    }`}>
                                      {isSelected && (
                                        <CheckCircleIcon className="w-3 h-3 text-white" />
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Info Agenzia */}
                                  <div className="pr-8">
                                    <div className="flex items-center space-x-3 mb-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                        <BuildingOfficeIcon className="h-5 w-5 text-white" />
                                      </div>
                                      <div>
                                        <h3 className={`text-lg font-bold ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                                          {agenzia.nome}
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                          {agenzia.citta || 'Città non specificata'}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Statistiche */}
                                    <div className="grid grid-cols-3 gap-3">
                                      <div className="text-center p-2 bg-white/70 rounded-xl">
                                        <div className={`text-lg font-bold ${isSelected ? 'text-green-600' : 'text-blue-600'}`}>
                                          {stats.praticheCompletate || 0}
                                        </div>
                                        <div className="text-xs text-gray-500">Completate</div>
                                      </div>
                                      <div className="text-center p-2 bg-white/70 rounded-xl">
                                        <div className={`text-lg font-bold ${isSelected ? 'text-green-600' : 'text-orange-600'}`}>
                                          {stats.praticheInLavorazione || 0}
                                        </div>
                                        <div className="text-xs text-gray-500">In Corso</div>
                                      </div>
                                      <div className="text-center p-2 bg-white/70 rounded-xl">
                                        <div className={`text-lg font-bold ${isSelected ? 'text-green-600' : 'text-purple-600'}`}>
                                          {stats.tempoMedioGiorni || 0}
                                        </div>
                                        <div className="text-xs text-gray-500">Giorni Medi</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Note Assegnazione */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Note Assegnazione
                    </label>
                    <textarea
                      className="w-full p-4 border border-gray-200/50 rounded-2xl bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 resize-none"
                      rows="3"
                      value={noteAssegnazione}
                      onChange={(e) => setNoteAssegnazione(e.target.value)}
                      placeholder="Note aggiuntive per l'assegnazione..."
                    />
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex justify-between items-center p-8 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                <button
                  type="button"
                  className="px-6 py-3 bg-white/70 border border-gray-200/50 text-gray-700 font-semibold rounded-2xl hover:bg-white/90 hover:shadow-md transition-all duration-200"
                  onClick={closeAssignModal}
                >
                  Annulla
                </button>
                
                <button
                  type="button"
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-2xl hover:from-green-700 hover:to-green-800 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAssegnazione}
                  disabled={loadingAssegnazione || !selectedAgenzia}
                >
                  {loadingAssegnazione ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Assegnazione...
                    </div>
                  ) : 'Assegna Lead'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeadModals; 